import {DateArg} from "date-fns";
import {useCallback, useEffect, useMemo, useRef} from "react";
// eslint-disable-next-line import/no-unresolved
import {RequiredKeysOf} from "type-fest";
import {z} from "zod/v4";

// https://stackoverflow.com/questions/69019873/how-can-i-get-typed-object-entries-and-object-fromentries-in-typescript
export function objectFromEntries<const T extends readonly (readonly [PropertyKey, unknown])[]>(
    entries: T
): { [K in T[number] as K[0]]: K[1] } {
    return Object.fromEntries(entries) as { [K in T[number] as K[0]]: K[1] };
}

export function objectKeys<const T extends Record<PropertyKey, unknown>>(o: T) {
    return Object.keys(o) as RequiredKeysOf<T>[];
}

export interface Keyed<T> {
    ix: number;
    key: string;
    elem: T;
}

export function keyify<T>(ar: T[]): Keyed<T>[] {
    const n = ar.length;
    const rv: Keyed<T>[] = new Array<Keyed<T>>(n);
    for (let i = 0; i < n; ++i) {
        rv[i] = {ix: i, key: String(i), elem: ar[i]!};
    }
    return rv;
}

export function cleanse<T>(dirty?: T[]): NonNullable<T>[] {
    if (!dirty) {
        return NADA;
    }
    return dirty.filter(maybe => !!maybe) as NonNullable<T>[];
}

export const NADA = [];

// export interface Ided extends Record<string, unknown> {
//     id: string;
// }

export const Ided = z.object({
    id: z.string(),
});
export type Ided = z.infer<typeof Ided>;

export function isEqualById(a: Ided, b: Ided) {
    return a.id === b.id;
}

export type Comparator<T> = (a: T, b: T) => number;

export function cmpDeadbeef(): number {
    throw new Error("comparing dead beef");
}

export function invertComparator<T>(under: Comparator<T>): Comparator<T> {
    return (a, b) => (-under(a, b));
}

export function cmpById<T extends Ided>(a: T, b: T): number {
    const an = Number.parseInt(a.id);
    const bn = Number.parseInt(b.id);
    return an - bn;
}

export function cmpByCode<T extends { code: string }>(a: T, b: T): number {
    const acode = a.code;
    const bcode = b.code;
    const delta = acode.localeCompare(bcode);
    return delta;
}

export type Permutation<U, T = U> = [U] extends [never]
    ? []
    : T extends unknown
        ? [T, ...Permutation<Exclude<U, T>>]
        : never;

// export interface Touchable {
//     lastTouched: Date | string;
// }
//
// export function cmpTouchableNewestFirst(a: Touchable, b: Touchable): number {
//     const delta = differenceInMilliseconds(b.lastTouched, a.lastTouched);
//     return delta;
// }
//
// export function cmpTouchableOldestFirst(a: Touchable, b: Touchable): number {
//     const delta = differenceInMilliseconds(a.lastTouched, b.lastTouched);
//     return delta;
// }

export interface ModTimes {
    lastModified: DateArg<Date>;
    lastUpdated: DateArg<Date>;
}

// export interface Tiered<TierTypeMap> {
//     tier: keyof TierTypeMap;
// }

export interface MakeTieredComparatorProps<T, TierTypeMap> {
    tiers: (keyof TierTypeMap)[];
    extractTier: (from: T) => keyof TierTypeMap;
}

export function makeTieredComparator<T, TierTypeMap>({
                                                         tiers,
                                                         extractTier
                                                     }: MakeTieredComparatorProps<T, TierTypeMap>): Comparator<T> {
    const tierIndexes: Partial<Record<keyof TierTypeMap, number>> = {}
    for (let i = 0; i < tiers.length; ++i) {
        tierIndexes[tiers[i]!] = i;
    }

    return (a: T, b: T) => {
        const atier = extractTier(a);
        const aT = tierIndexes[atier];
        if (aT === undefined) {
            return Number.NEGATIVE_INFINITY;
        }
        const btier = extractTier(b);
        const bT = tierIndexes[btier];
        if (bT === undefined) {
            return Number.POSITIVE_INFINITY;
        }

        const delta = aT - bT;
        return delta;
    };
}

export function makeCascadingComparator<T>(cmpers: Comparator<T>[]): Comparator<T> {
    return (a, b) => {
        for (const cmp of cmpers) {
            const delta = cmp(a, b);
            if (delta !== 0) {
                return delta;
            }
        }
        return 0;
    }
}

// export function makeTieredTouchableNewestFirstComparator<TierTypeMap>(tiers: (keyof TierTypeMap)[]): Comparator<Tiered<TierTypeMap> & Touchable> {
//     return makeCascadingComparator<T>([
//         makeTieredComparator<TierTypeMap>(tiers),
//         cmpTouchableNewestFirst,
//     ]);
// }
//     const cmpTier = makeTieredComparator(tiers);
//
//     return (a: Tiered<TierTypeMap> & Touchable, b: Tiered<TierTypeMap> & Touchable) => {
//         const deltaTier = cmpTier(a, b);
//         if (deltaTier !== 0) {
//             return deltaTier;
//         }
//
//         const deltaTouched = cmpTouchableNewestFirst(a, b);
//         return deltaTouched;
//     }
// }

export function makeSortSieve<T>(cmp: Comparator<T>, pivot: T): (maybe: T) => boolean {
    return (maybe: T) => {
        const delta = cmp(pivot, maybe);
        return delta > 0;
    };
}

// export type TouchableOrderings<T, OrderingTypeMap>
//     = Record<keyof OrderingTypeMap, IndirectArray<T>>;
//
// export type TouchableComparators<T, OrderingTypeMap>
//     = Record<keyof OrderingTypeMap, Comparator<T>>;

// export interface UseTouchableListArgs<T, KT, OrderingTypeMap> {
//     extractKey: (v: T) => KT;
//     sortComparators: TouchableComparators<T, OrderingTypeMap>;
// }
//
// interface TouchableListState<T, KT, OrderingTypeMap> {
//     byKey: Map<KT, T>;
//     orderings: TouchableOrderings<T, OrderingTypeMap>;
// }

// interface DynaMapTouchAction<T> {
//     action: "touch";
//     value: T;
// }
//
// interface DynaMapPatchAction<T, KT> {
//     action: "patch";
//     key: KT;
//     apply: (prev: T | undefined) => T | undefined;
// }
//
// interface DynaMapEvictAction<KT> {
//     action: "evict";
//     key: KT;
// }
//
// interface DynaMapClearAction {
//     action: "clear";
// }
//
// export type DynaMapAction<T, KT> =
//     DynaMapTouchAction<T>
//     | DynaMapPatchAction<T, KT>
//     | DynaMapEvictAction<KT>
//     | DynaMapClearAction;
//
// interface DynaMapListenerArgs<T, KT> extends Transition<T | undefined> {
//     key: KT;
// }
//
// export interface DynaMapApi<T, KT> {
//     extractKey: (v: T) => KT;
//     range: () => MapIterator<T> | T[];
//     dispatchAction: (action: DynaMapAction<T, KT>) => void;
//     getByKey: (k: KT) => T | undefined;
//     listenChanges: (listener: (args: DynaMapListenerArgs<T, KT>) => void) => Unlistener;
// }
//
export function dieWithoutProvider() {
    throw new Error("no provider in sight");
}

//
// export const deadbeefDynaMapApi = {
//     extractKey: dieWithoutProvider as () => any,
//     range: dieWithoutProvider as () => any[],
//     dispatchAction: dieWithoutProvider,
//     getByKey: dieWithoutProvider as () => any,
//     listenChanges: dieWithoutProvider as () => Unlistener,
// };
//
// interface DynaMapState<T, KT> {
//     byKey?: Map<KT, T>;
// }
//
// export interface DynaMapProviderProps<T, KT> {
//     Context: Context<DynaMapApi<T, KT>>;
//     extractKey: (item: T) => KT;
// }
//
// type DynaMapListener<T, KT> = (args: DynaMapListenerArgs<T, KT>) => void;

// export function DynaMapProvider<T, KT>({
//                                            Context,
//                                            extractKey,
//                                            children
//                                        }: PropsWithChildren<DynaMapProviderProps<T, KT>>) {
//     const listeners = useListenerList<DynaMapListenerArgs<T, KT>>();
//
//     const reduceState = useCallback(
//         (prevState: DynaMapState<T, KT>, action: DynaMapAction<T, KT>) => {
//             const byKey = prevState.byKey ?? new Map<KT, T>();
//             switch (action.action) {
//                 case "touch": {
//                     const next = action.value;
//                     const key = extractKey(next);
//                     const prev = byKey.get(key);
//                     byKey.set(key, next);
//                     listeners.notify({key, prev, next});
//                     return {byKey};
//                 }
//
//                 case "patch": {
//                     const key = action.key;
//                     const prev = byKey.get(key);
//                     const next = action.apply(prev);
//                     if (next && next !== prev) {
//                         byKey.set(key, next)
//                         listeners.notify({key, prev, next});
//                         return {byKey};
//                     } else {
//                         return prevState;
//                     }
//                 }
//
//                 case "evict": {
//                     const key = action.key;
//                     const value = byKey.get(key);
//                     if (!value) {
//                         return prevState;
//                     }
//                     byKey.delete(key);
//                     listeners.notify({key, prev: value, next: undefined});
//                     return {byKey};
//                 }
//
//                 case "clear": {
//                     return {};
//                 }
//             }
//         }, [extractKey, listeners]);
//
//     const [state, dispatchState] = useReducer(reduceState, {});
//
//     const addListener = useCallback((listener: DynaMapListener<T, KT>) => {
//         return listeners.listen(listener);
//     }, [listeners]);
//
//     const dispatchAction = useCallback((args) => {
//         dispatchState(args);
//     }, []);
//
//     const api = useMemo<DynaMapApi<T, KT>>(() => ({
//         extractKey,
//         range: () => state.byKey?.values() ?? [],
//         // dispatchAction: dispatchState,
//         dispatchAction,
//         getByKey: (key) => state.byKey?.get(key),
//         listenChanges: addListener,
//     }), [addListener, dispatchAction, extractKey, state.byKey]);
//
//     return <Context value={api}>{children}</Context>;
// }

// const nextOrderings = {} as TouchableOrderings<T, OrderingTypeMap>;
// for (const orderingName in prevState.orderings) {
//     const items = prevState.orderings[orderingName].items;
//     const cmp = sortComparators[orderingName];
//     const ix = sortedIndexWith(items, makeSortSieve<T>(cmp, value));
//     const xrec = items[ix];
//     const xkey = extractKey(xrec);
//     if (xkey === key) {
//         items.splice(ix, 1);
//     }
//     nextOrderings[orderingName] = {items};
// }
//
// export interface SpawnDynaMapArgs<T, KT> {
//     extractKey: (item: T) => KT;
// }
//
// export interface PublicDynaMapState<T> {
//     ordered?: T[];
//     updatesSinceReset?: number;
// }
//
// interface InternalDynaMapState<T, KT> extends PublicDynaMapState<T> {
//     byKey?: Map<KT, T>;
//     cmp?: Comparator<T>;
// }
//
// export interface DynaMapApi<T, KT> {
//     getByKey: (key: KT) => T | undefined;
//     replace: (next: T) => void;
//     patch: (key: KT, apply: (prev: T | undefined) => T | undefined) => void;
//     evict: (key: KT) => void;
//     prune: (keepKeys: KT[]) => void;
//     clear: () => void;
//     range: () => MapIterator<[KT, T]>;
//     orderBy: (cmp: Comparator<T>) => void;
//     clearUpdateCounter: () => void;
// }
//
// export interface DynaMapStoreState<T, KT> {
//     state: PublicDynaMapState<T>;
//     api: DynaMapApi<T, KT>;
//
//     _istate: InternalDynaMapState<T, KT>;
// }
//
// export function makeDynaMapStoreSpawner<T extends {}, KT>({extractKey}: SpawnDynaMapArgs<T, KT>) {
//     const rv: StateCreator<DynaMapStoreState<T, KT>> = (set, get) => {
//         const spliceOut = ({cmp, pordered, key, prev}: { cmp: Comparator<T>, pordered: T[], key: KT, prev: T }) => {
//             const pix = sortedIndexWith(pordered, makeSortSieve<T>(cmp, prev));
//             const pkey = extractKey(pordered[pix]);
//             if (pkey === key) {
//                 return pordered.toSpliced(pix, 1);
//             } else {
//                 return pordered;
//             }
//         };
//
//         const spliceIn = ({cmp, ordered, next}: { cmp: Comparator<T>, ordered: T[], next: T }) => {
//             const nextix = sortedIndexWith(ordered, makeSortSieve(cmp, next));
//             ordered.splice(nextix, 0, next);
//         };
//
//         const performOrderedReplace = ({cmp, pordered, key, prev, next}: {
//             cmp: Comparator<T>,
//             pordered: T[] | undefined,
//             key: KT,
//             prev: T | undefined,
//             next: T
//         }) => {
//             if (!pordered) {
//                 return [next];
//             }
//
//             const splicedOut = prev
//                 ? spliceOut({cmp, pordered, key, prev})
//                 : pordered;
//
//             const nordered = splicedOut !== pordered ? splicedOut : [...pordered];
//             spliceIn({cmp, ordered: nordered, next});
//             return nordered;
//         };
//
//         const getByKey = (key: KT) => {
//             const rv = get()._istate.byKey?.get(key);
//             return rv;
//         };
//
//         const replace = (next: T) => {
//             const key = extractKey(next);
//             const ps = get();
//             const prev = ps._istate.byKey?.get(key);
//             if (!Object.is(next, prev)) {
//                 const newByKey = new Map<KT, T>(ps._istate.byKey);
//                 newByKey.set(key, next);
//                 const cmp = ps._istate.cmp;
//                 const ns: Partial<DynaMapStoreState<T, KT>> = {
//                     _istate: {...ps._istate, byKey: newByKey},
//                     state: {
//                         updatesSinceReset: (ps.state.updatesSinceReset ?? 0) + 1,
//                         ordered: cmp
//                             ? performOrderedReplace({cmp, pordered: ps.state.ordered, key, prev, next})
//                             : undefined
//                     }
//                 };
//
//                 set(ns);
//             }
//         };
//
//         const patch = (key: KT, apply: (prev: T | undefined) => T | undefined) => {
//             const ps = get();
//             const byKey = ps._istate.byKey;
//             const prev = byKey?.get(key);
//             const next = apply(prev);
//             if (!Object.is(next, prev)) {
//                 const newByKey = new Map<KT, T>(byKey);
//                 const cmp = ps._istate.cmp;
//                 let nordered: T[] | undefined;
//                 if (next) {
//                     newByKey.set(key, next);
//                     if (cmp) {
//                         nordered = performOrderedReplace({cmp, pordered: ps.state.ordered, key, prev, next});
//                     }
//                 } else {
//                     newByKey.delete(key);
//                     if (cmp && ps.state.ordered && prev) {
//                         nordered = spliceOut({cmp, pordered: ps.state.ordered, key, prev});
//                     }
//                 }
//                 const ns: Partial<DynaMapStoreState<T, KT>> = {
//                     _istate: {...ps._istate, byKey: newByKey},
//                     state: {
//                         updatesSinceReset: (ps.state.updatesSinceReset ?? 0) + 1,
//                         ordered: nordered,
//                     }
//                 };
//
//                 set(ns);
//             }
//         };
//
//         const evict = (key: KT) => {
//             const ps = get();
//             const psi = ps._istate;
//             const prev = psi.byKey?.get(key);
//             if (prev) {
//                 const newByKey = new Map<KT, T>(psi.byKey);
//                 newByKey.delete(key);
//                 const cmp = ps._istate.cmp;
//                 const ns: Partial<DynaMapStoreState<T, KT>> = {
//                     _istate: {...psi, byKey: newByKey},
//                     state: {
//                         updatesSinceReset: (ps.state.updatesSinceReset ?? 0) + 1,
//                         ordered: (cmp && ps.state.ordered)
//                             ? spliceOut({cmp, pordered: ps.state.ordered, key, prev})
//                             : undefined
//                     }
//                 };
//
//                 set(ns);
//             }
//         };
//
//         const clear = () => {
//             const ps = get();
//             const ns: Partial<DynaMapStoreState<T, KT>> = {
//                 _istate: {...ps._istate, byKey: new Map<KT, T>()},
//                 state: {updatesSinceReset: 0, ordered: ps._istate.cmp ? [] : undefined}
//             };
//
//             set(ns);
//         };
//
//         const range = () => {
//             const byKey = get()._istate.byKey ?? new Map<KT, T>();
//             return byKey.entries();
//         };
//
//         const prune = (keepKeys: KT[]) => {
//             const trash = new Map<KT, T>(range());
//             for (const key of keepKeys) {
//                 trash.delete(key);
//             }
//             for (const trashKey of trash.keys()) {
//                 evict(trashKey);
//             }
//         };
//
//         const orderBy = (cmp: Comparator<T>) => {
//             const ps = get();
//             const nordered = Array.from(ps._istate.byKey?.values() ?? []);
//             nordered.sort(cmp);
//
//             set({_istate: {...ps._istate, cmp}, state: {...ps.state, ordered: nordered}});
//         };
//
//         const clearUpdateCounter = () => {
//             set(prev => ({state: {...prev.state, updatesSinceReset: 0}}));
//         };
//
//         const api: DynaMapApi<T, KT> = {
//             getByKey, replace, patch, evict, clear, range, prune, orderBy, clearUpdateCounter,
//         };
//
//         return {api, state: {}, _istate: {}};
//     };
//     return rv;
// }

// export interface UseDynaMapOrderingProps<T, KT> {
//     dmapi: DynaMapApi<T, KT>;
//     cmp: Comparator<T>;
// }
//
// export function useDynaMapOrdering<T extends object, KT>({dmapi, cmp}: UseDynaMapOrderingProps<T, KT>): T[] {
//     const [state, setState] = useState<T[]>(Array.from(dmapi.range()).sort(cmp));
//
//     const listen = useCallback(({key, prev, next}: DynaMapListenerArgs<T, KT>) => {
//         setState(prevState => {
//             let nextState: T[] | undefined;
//             if (prev) {
//                 const previx = sortedIndexWith(prevState, makeSortSieve<T>(cmp, prev));
//                 const prevKey = dmapi.extractKey(prevState[previx]);
//                 if (prevKey === key) {
//                     nextState = prevState.toSpliced(previx, 1);
//                 }
//             }
//             nextState ??= [...prevState];
//             if (next) {
//                 const nextix = sortedIndexWith(nextState, makeSortSieve(cmp, next));
//                 nextState.splice(nextix, 0, next);
//             }
//             return nextState;
//         });
//     }, [cmp, dmapi]);
//
//     const startListening = useCallback(() =>
//         dmapi.listenChanges(listen), [dmapi, listen]);
//     useManagedListener(startListening);
//
//     return state;
// }
//
// export type UseTouchableListResult<T, KT, OrderingTypeMap>
//     = TouchableListOrdering<T, OrderingTypeMap> & TouchableListApi<T, KT>;
//
// export function useTouchableList<T, KT, OrderingTypeMap>(
//     {
//         extractKey,
//         sortComparators,
//         // onTouched,
//         // onStableOrderChanged,
//         // onActiveOrderChanged,
//     }: UseTouchableListArgs<T, KT, OrderingTypeMap>): UseTouchableListResult<T, KT, OrderingTypeMap> {
//
//     const makeBlankState = useCallback(() => {
//         const rv: TouchableListState<T, KT, OrderingTypeMap> = {
//             byKey: new Map<KT, T>(),
//             orderings: Object.fromEntries(Object.keys(sortComparators)
//                 .map(orderingName => ([orderingName, {items: [] as T[]}]))) as TouchableOrderings<T, OrderingTypeMap>,
//         };
//         return rv;
//     }, [sortComparators]);
//
//     const performTouch = useCallback(
//         (prevState: TouchableListState<T, KT, OrderingTypeMap>, {key, prev, next}: {
//             key: KT;
//             prev: T | undefined;
//             next: T
//         }) => {
//             if (prev === next) {
//                 return prevState;
//             }
//             const {byKey, orderings} = prevState;
//             byKey.set(key, next);
//             const newOrderings: Partial<TouchableOrderings<T, OrderingTypeMap>> = {};
//             for (const orderingName in orderings) {
//                 const items = orderings[orderingName].items;
//                 const cmp = sortComparators[orderingName];
//                 if (prev) {
//                     const previx = sortedIndexWith(items, makeSortSieve<T>(cmp, prev));
//                     const prevKey = extractKey(items[previx]);
//                     if (prevKey === key) {
//                         items.splice(previx, 1);
//                     }
//                 }
//                 const nextix = sortedIndexWith(items, makeSortSieve(cmp, next));
//                 items.splice(nextix, 0, next);
//                 newOrderings[orderingName] = {items};
//             }
//             const rv = {byKey, orderings: newOrderings as TouchableOrderings<T, OrderingTypeMap>};
//             return rv;
//         }, [extractKey, sortComparators]);
//
//     const reduceState = useCallback(
//         (prevState: TouchableListState<T, KT, OrderingTypeMap>, action: TouchableListAction<T, KT>) => {
//             switch (action.action) {
//                 case "touch": {
//                     const next = action.value;
//                     const key = extractKey(next);
//                     const prev = prevState.byKey.get(key);
//                     const rv: TouchableListState<T, KT, OrderingTypeMap>
//                         = performTouch(prevState, {key, prev, next});
//                     return rv;
//                 }
//
//                 case "patch": {
//                     const prev = prevState.byKey.get(action.key);
//                     const next = action.apply(prev);
//                     const rv: TouchableListState<T, KT, OrderingTypeMap>
//                         = performTouch(prevState, {key: action.key, prev, next});
//                     return rv;
//                 }
//
//                 case "evict": {
//                     const byKey = prevState.byKey;
//                     const key = action.key;
//                     const value = prevState.byKey.get(key);
//                     if (!value) {
//                         return prevState;
//                     }
//                     const nextOrderings = {} as TouchableOrderings<T, OrderingTypeMap>;
//                     for (const orderingName in prevState.orderings) {
//                         const items = prevState.orderings[orderingName].items;
//                         const cmp = sortComparators[orderingName];
//                         const ix = sortedIndexWith(items, makeSortSieve<T>(cmp, value));
//                         const xrec = items[ix];
//                         const xkey = extractKey(xrec);
//                         if (xkey === key) {
//                             items.splice(ix, 1);
//                         }
//                         nextOrderings[orderingName] = {items};
//                     }
//                     byKey.delete(key);
//                     const rv: TouchableListState<T, KT, OrderingTypeMap> = {byKey, orderings: nextOrderings};
//                     return rv;
//                 }
//
//                 case "clear": {
//                     return makeBlankState();
//                 }
//             }
//         }, [extractKey, makeBlankState, performTouch, sortComparators]);
//
//     const [state, dispatchState] = useReducer(reduceState, makeBlankState());
//
//     const touch = useCallback((v: T) => {
//         dispatchState({action: "touch", value: v});
//     }, []);
//
//     const patch = useCallback((k: KT, apply: (prev: T | undefined) => T) => {
//         dispatchState({action: "patch", key: k, apply})
//     }, []);
//
//     const evict = useCallback((k: KT) => {
//         dispatchState({action: "evict", key: k});
//     }, []);
//
//     const clear = useCallback(() => {
//         dispatchState({action: "clear"});
//     }, []);
//
//     const getByKey = useCallback((k: KT) => {
//         const rv = state.byKey.get(k);
//         return rv;
//     }, [state.byKey]);
//
//     const rv = useMemo(() => {
//         const rv = {
//             orderings: state.orderings,
//             touch, patch, evict, clear, getByKey
//         };
//         return rv;
//     }, [clear, evict, getByKey, patch, state.orderings, touch]);
//
//     return rv;
// }
//
// export interface TieredTouchableListItem<T, TierTypeMap> extends Touchable, Tiered<TierTypeMap> {
//     value: T;
// }
//
// export interface UseSimpleTieredTouchableListArgs<T, KT extends string, TierTypeMap> {
//     extractKey: UseTouchableListArgs<T, KT, { active: never }>["extractKey"];
//     // this is the order that tiers will be iterated over, and must never change
//     tierNames: (keyof TierTypeMap)[];
// }
//
//
// export interface TieredTouchableListApi<T, TierTypeMap extends string | symbol | number, KT = string> extends TouchableListApi<T, KT> {
//     // just call house again to move something between tiers
//     // you should house something before touching it, but if you don't, it will go
//     // into whatever tier 0
//     house: (item: T, seg: TierTypeMap) => void,
//
//     // returns null if the element is nowhere
//     findTierFor: (item: T) => TierTypeMap | null,
// }
//
// export interface UseTieredTouchableListResult<T, TierTypeMap extends string | symbol | number, KT = string>
//     extends TieredTouchableListApi<T, TierTypeMap, KT> {
//     tiers: Record<TierTypeMap, UseTouchableListResult<T, KT>>;
// }
//

// export function useSimpleTieredTouchableList<T extends Ided, KT extends string, TierTypeMap>(props: UseSimpleTieredTouchableListArgs<T, KT, TierTypeMap>):
//     UseTouchableListResult<TieredTouchableListItem<T, TierTypeMap>, KT, { active: unknown }> {
//     const sortComparators = useMemo(() => ({
//         active: makeTieredTouchableNewestFirstComparator(props.tierNames)
//     }), [props.tierNames]);
//     const extractKey = useCallback((elem: TieredTouchableListItem<T, TierTypeMap>) => {
//         const extractUnder = props.extractKey;
//         const rv = extractUnder(elem.value);
//         return rv;
//     }, [props.extractKey]);
//     const utl = useTouchableList({
//         extractKey, sortComparators,
//     });
//     return utl;
// }


// export function useTieredTouchableList<T, TierTypeMap extends string, KT = string>(
//     {
//         extractKey,
//         extractStableComparisonProxy,
//         tierNames
//     }: UseTieredTouchableListArgs<T, TierTypeMap, KT>): UseTieredTouchableListResult<T, TierTypeMap, KT> {
//
//     const usedTiers = Object.fromEntries(tierNames.map(name => ([name, useTouchableList({
//         extractKey, extractStableComparisonProxy
//     })])));
//
//         const tiers = useMemo(() => {
//             const rv = tierNames.map(name => usedTiers[name])
//         })
// }

export type Listener<T> = (args: T) => void;
export type Unlistener = () => void;

export interface ListenerListManager<T> {
    notify: (args: T) => void;
    listen: (listener: Listener<T>) => Unlistener;
    clear: () => void;
}

// for use in situations without a react context
export function listenerListAdd<T>(listeners: Listener<T>[], newb: Listener<T>) {
    const xix = listeners.indexOf(newb);
    if (xix < 0) {
        listeners.push(newb);
    }
    return () => {
        const ix = listeners.indexOf(newb);
        if (ix >= 0) {
            listeners.splice(ix, 1);
        }
    };
}

export function useListenerList<T>() {
    const listeners = useRef<Listener<T>[]>([]);
    const manager = useMemo<ListenerListManager<T>>(() => {
        return {
            notify: (args) => {
                listeners.current.forEach(listener => {
                    listener(args);
                });
            },
            listen: (listener) => {
                const xix = listeners.current.indexOf(listener);
                if (xix < 0) {
                    listeners.current.push(listener);
                }
                return () => {
                    const ix = listeners.current.indexOf(listener);
                    if (ix >= 0) {
                        listeners.current.splice(ix, 1);
                    }
                };
            },
            clear: () => {
                listeners.current = [];
            }
        }
    }, []);
    return manager;
}


export interface DeferredCleanupSetApi {
    add: (ul: Unlistener) => void;
    remove: (ul: Unlistener) => void;
    clear: () => void;
    perform: () => void;
}

export function useDeferredCleanup(): DeferredCleanupSetApi {
    const tasksRef = useRef<Set<Unlistener>>(new Set<Unlistener>());

    const add = useCallback((ul: Unlistener) => {
        tasksRef.current.add(ul);
    }, []);

    const remove = useCallback((ul: Unlistener) => {
        tasksRef.current.delete(ul);
    }, []);

    const clear = useCallback(() => {
        tasksRef.current = new Set<Unlistener>();
    }, []);

    const perform = useCallback(() => {
        tasksRef.current.values().forEach(ul => {
            ul();
        });
        tasksRef.current.clear();
    }, []);

    useEffect(() => {
        return perform;
    }, [perform]);

    const api = useMemo(() => ({add, remove, clear, perform}),
        [add, clear, perform, remove]);

    return api;
}

export function useManagedListener(startListening: () => Unlistener) {
    const unlistenRef = useRef<Unlistener | undefined>(undefined);
    useEffect(() => {
        if (unlistenRef.current) {
            unlistenRef.current();
        }
        const unlisten = startListening();
        unlistenRef.current = () => {
            unlisten();
            unlistenRef.current = undefined;
        }
        return unlisten;
    }, [startListening]);
}

export function absorb<T>(holder: T[], food: T | T[]) {
    if (Array.isArray(food)) {
        holder.push(...food);
    } else {
        holder.push(food);
    }
}

export function union<T>(...foods: T[][]) {
    const set = new Set<T>();
    for (const food of foods) {
        for (const item of food) {
            set.add(item);
        }
    }
    const rv = Array.from(set.values());
    return rv;
}

export function indexesOf(ar: unknown[]): number[] {
    // const len = ar.length;
    // const trampy = Array(len);
    // const rv = Array.from(trampy.keys());
    // return rv;
    return Array.from(Array(ar.length).keys());
}

export interface Armap<T extends Ided> {
    ar: T[];
    byId: Map<string, T>
}

export function armapify<T extends Ided>(ar: T[]): Armap<T> {
    return {ar, byId: new Map<string, T>(ar.map(elem => ([elem.id, elem] as const)))};
}


